/**
 * WorkflowView Page
 *
 * View workflow details and steps (read-only)
 */

import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  GitBranch,
  ArrowLeft,
  Edit2,
  Clock,
  User,
  CheckCircle,
  FileText,
  Star,
  Activity,
  Loader2,
} from 'lucide-react'

import { Button, Alert } from '@components/common'
import useWorkflowStore from '@store/useWorkflowStore'

import './WorkflowView.scss'

// Role display names
const ROLE_NAMES = {
  'company-admin': 'Company Admin',
  'project-manager': 'Project Manager',
  management: 'Management',
  employee: 'Employee',
}

const WorkflowView = () => {
  const navigate = useNavigate()
  const { id } = useParams()

  // Store
  const {
    selectedWorkflow: currentWorkflow,
    loading: isLoading,
    error,
    fetchWorkflowById,
  } = useWorkflowStore()

  // Clear error function - local since store doesn't have one
  const clearError = () => {}

  // Fetch workflow on mount
  useEffect(() => {
    fetchWorkflowById(id)
  }, [id, fetchWorkflowById])

  // Get status badge variant
  const getStatusVariant = (status) => {
    switch (status) {
      case 'active':
        return 'success'
      case 'disabled':
        return 'warning'
      case 'archived':
        return 'secondary'
      default:
        return 'secondary'
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="workflow-view-page">
        <div className="loading-container">
          <Loader2 className="spin" size={40} />
          <p>Loading workflow...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="workflow-view-page">
        <Alert type="error" title="Error" message={error} dismissible onDismiss={clearError} />
        <div style={{ marginTop: 'var(--spacing-lg)' }}>
          <Button variant="secondary" onClick={() => navigate('/workflows')}>
            <ArrowLeft size={16} /> Back to Workflows
          </Button>
        </div>
      </div>
    )
  }

  // Not found state
  if (!currentWorkflow) {
    return (
      <div className="workflow-view-page">
        <Alert
          type="error"
          title="Workflow Not Found"
          message="The workflow you're looking for doesn't exist or has been deleted."
        />
        <div style={{ marginTop: 'var(--spacing-lg)' }}>
          <Button variant="secondary" onClick={() => navigate('/workflows')}>
            <ArrowLeft size={16} /> Back to Workflows
          </Button>
        </div>
      </div>
    )
  }

  const {
    name,
    description,
    status,
    is_default,
    steps = [],
    projects_count = 0,
    creator_first_name,
    creator_last_name,
    created_at,
    updated_at,
  } = currentWorkflow

  const creatorName =
    creator_first_name && creator_last_name
      ? `${creator_first_name} ${creator_last_name}`
      : 'Unknown'

  return (
    <div className="workflow-view-page">
      {/* Header */}
      <div className="workflow-view-header">
        <div className="workflow-view-header__left">
          <button
            className="workflow-view-header__back"
            onClick={() => navigate('/workflows')}
          >
            <ArrowLeft size={16} /> Back to Workflows
          </button>
          <div className="workflow-view-header__title-row">
            <div className="workflow-view-header__icon">
              <GitBranch size={28} />
            </div>
            <div className="workflow-view-header__info">
              <div className="workflow-view-header__title-badges">
                <h1 className="workflow-view-header__title">{name}</h1>
                {(is_default === '1' || is_default === 1) && (
                  <span className={`badge badge--primary`}>
                    <Star size={12} /> Default
                  </span>
                )}
                <span className={`badge badge--${getStatusVariant(status)}`}>
                  {status?.charAt(0).toUpperCase() + status?.slice(1)}
                </span>
              </div>
              {description && (
                <p className="workflow-view-header__subtitle">{description}</p>
              )}
            </div>
          </div>
        </div>
        <div className="workflow-view-header__actions">
          <Button
            variant="primary"
            onClick={() => navigate(`/workflows/${id}/edit`)}
          >
            <Edit2 size={16} /> Edit Workflow
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="workflow-view-stats">
        <div className="workflow-view-stats__card">
          <div className="workflow-view-stats__icon workflow-view-stats__icon--steps">
            <Activity size={20} />
          </div>
          <div className="workflow-view-stats__content">
            <span className="workflow-view-stats__value">{steps.length}</span>
            <span className="workflow-view-stats__label">Approval Steps</span>
          </div>
        </div>
        <div className="workflow-view-stats__card">
          <div className="workflow-view-stats__icon workflow-view-stats__icon--projects">
            <FileText size={20} />
          </div>
          <div className="workflow-view-stats__content">
            <span className="workflow-view-stats__value">{projects_count}</span>
            <span className="workflow-view-stats__label">Active Projects</span>
          </div>
        </div>
        <div className="workflow-view-stats__card">
          <div className="workflow-view-stats__icon workflow-view-stats__icon--sla">
            <Clock size={20} />
          </div>
          <div className="workflow-view-stats__content">
            <span className="workflow-view-stats__value">
              {steps.reduce((sum, step) => sum + parseInt(step.sla_hours || 0, 10), 0)}h
            </span>
            <span className="workflow-view-stats__label">Total SLA Time</span>
          </div>
        </div>
        <div className="workflow-view-stats__card">
          <div className="workflow-view-stats__icon workflow-view-stats__icon--evidence">
            <CheckCircle size={20} />
          </div>
          <div className="workflow-view-stats__content">
            <span className="workflow-view-stats__value">
              {steps.filter((s) => s.evidence_required === '1' || s.evidence_required === 1).length}
            </span>
            <span className="workflow-view-stats__label">Evidence Required</span>
          </div>
        </div>
      </div>

      {/* Workflow Steps */}
      <div className="workflow-view-section">
        <h2 className="workflow-view-section__title">Approval Steps</h2>
        <div className="workflow-steps-timeline">
          {steps.length === 0 ? (
            <div className="workflow-steps-empty">
              <p>No steps defined for this workflow.</p>
            </div>
          ) : (
            steps.map((step, index) => (
              <div key={step.id || index} className="workflow-step-item">
                <div className="workflow-step-item__number">{index + 1}</div>
                <div className="workflow-step-item__content">
                  <div className="workflow-step-item__header">
                    <h3 className="workflow-step-item__name">{step.name}</h3>
                    <span className="badge badge--secondary">
                      {ROLE_NAMES[step.role] || step.role}
                    </span>
                  </div>
                  <div className="workflow-step-item__details">
                    <div className="workflow-step-item__detail">
                      <User size={14} />
                      <span>
                        {step.approver_first_name && step.approver_last_name
                          ? `${step.approver_first_name} ${step.approver_last_name}`
                          : 'Approver not assigned'}
                      </span>
                    </div>
                    <div className="workflow-step-item__detail">
                      <Clock size={14} />
                      <span>SLA: {step.sla_hours || 24} hours</span>
                    </div>
                    {(step.evidence_required === '1' || step.evidence_required === 1) && (
                      <div className="workflow-step-item__detail workflow-step-item__detail--evidence">
                        <CheckCircle size={14} />
                        <span>Evidence Required</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Metadata */}
      <div className="workflow-view-section workflow-view-section--metadata">
        <h2 className="workflow-view-section__title">Information</h2>
        <div className="workflow-metadata">
          <div className="workflow-metadata__item">
            <span className="workflow-metadata__label">Created by</span>
            <span className="workflow-metadata__value">{creatorName}</span>
          </div>
          <div className="workflow-metadata__item">
            <span className="workflow-metadata__label">Created on</span>
            <span className="workflow-metadata__value">
              {created_at
                ? new Date(created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : 'N/A'}
            </span>
          </div>
          <div className="workflow-metadata__item">
            <span className="workflow-metadata__label">Last updated</span>
            <span className="workflow-metadata__value">
              {updated_at
                ? new Date(updated_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WorkflowView
